import { get_member_attendance_summary, download_member_pdf } from "services/api-service";
import { REACT_APP_API_URL, API_ROUTES } from "../../config/api-config";
import React, { useState, useEffect, useCallback } from "react";

function AsyncImage({ src, alt, style, onClick }) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div
      onClick={onClick}
      style={{
        position: "relative",
        width: style.width || "100%",
        maxWidth: style.maxWidth || "130px",
        height: style.height || "130px",
        display: "inline-block",
        cursor: onClick ? "zoom-in" : "default",
      }}
    >
      {!hasLoaded && !error && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: style.borderRadius || "6px",
            backgroundColor: "#f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6b7280",
            fontSize: "11px",
            fontWeight: "500",
            border: "1px dashed #d1d5db",
            zIndex: 1,
          }}
        >
          ⏳ Loading...
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: style.borderRadius || "6px",
            backgroundColor: "#fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ef4444",
            fontSize: "11px",
            fontWeight: "500",
            border: "1px solid #fca5a5",
            zIndex: 1,
          }}
        >
          ⚠️ Failed
        </div>
      )}

      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          ...style,
          opacity: hasLoaded ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
        }}
        onLoad={() => setHasLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}

export default function MemberAttendanceTable() {
  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  const [activeView, setActiveView] = useState("daily");

  const [selectedDate, setSelectedDate] = useState(formattedToday);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [expandedRow, setExpandedRow] = useState(null);
  const [fullScreenImg, setFullScreenImg] = useState(null);

  // Filters
  const [selectedId, setSelectedId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedBehaviour, setSelectedBehaviour] = useState("");
  const [selectedPricing, setSelectedPricing] = useState("");

  // Range Report
  const [rangeStartDate, setRangeStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [rangeEndDate, setRangeEndDate] = useState(formattedToday);
  const [rangeData, setRangeData] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeSelectedBehaviour, setRangeSelectedBehaviour] = useState("");
  const [rangeSelectedCustomerName, setRangeSelectedCustomerName] = useState("");

  const fetchDataByDate = useCallback(async () => {
    if (!selectedDate) return;
    try {
      setLoading(true);
      // Fetch Master List (Filters applied locally)
      const payload = {
        date: selectedDate,
        page_number: 1,
        page_size: 5000, 
      };

      const response = await get_member_attendance_summary(payload);
      setCustomers(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const fetchDateRangeReport = useCallback(async () => {
    if (!rangeStartDate || !rangeEndDate) return;
    try {
      setRangeLoading(true);
      const start = new Date(rangeStartDate);
      const end = new Date(rangeEndDate);
      const allDates = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        allDates.push(d.toISOString().split("T")[0]);
      }

      const allData = [];
      for (const date of allDates) {
        try {
          const payload = {
            date: date,
            page_number: 1,
            page_size: 5000, 
          };
          const response = await get_member_attendance_summary(payload);
          allData.push(...(response?.data?.data || []));
        } catch (err) {}
      }
      setRangeData(allData);
    } catch (error) {
      console.error("Error fetching range:", error);
      setRangeData([]);
    } finally {
      setRangeLoading(false);
    }
  }, [rangeStartDate, rangeEndDate]);

  useEffect(() => {
    if (activeView === "daily") fetchDataByDate();
  }, [activeView, fetchDataByDate]);

  // Local filtering applied instantly in the browser
  const filteredCustomers = customers.filter((c) => {
    const matchesId = !selectedId || String(c.member_id) === String(selectedId);
    const matchesStatus = selectedStatus === "all" || c.status === selectedStatus;
    const matchesBehaviour = !selectedBehaviour || c.buying_behaviour === selectedBehaviour;
    const matchesPricing = !selectedPricing || c.pricing === selectedPricing;
    
    return matchesId && matchesStatus && matchesBehaviour && matchesPricing;
  });

  const uniqueCustomerIds = [...new Set(customers.map((c) => c.member_id))];
  const uniqueBehaviours = [...new Set(customers.map((c) => c.buying_behaviour).filter(Boolean))].sort();
  const uniquePricing = [...new Set(customers.map((c) => c.pricing).filter(Boolean))].sort();

  // Range Processing
  const processRangeData = () => {
    const reportMap = {};
    rangeData.forEach((record) => {
      const id = record.member_id;
      if (!reportMap[id]) {
        reportMap[id] = {
          customer_id: id,
          customer_name: record.member_name,
          buying_behaviour: record.buying_behaviour,
          pricing: record.pricing,
          total_visits: 0,
          total_hours: 0,
        };
      }
      if (record.status === "Present") {
        reportMap[id].total_visits += 1;
        reportMap[id].total_hours += record.total_visit_hours || 0;
      }
    });

    Object.values(reportMap).forEach((record) => {
      record.avg_duration = record.total_visits > 0 ? (record.total_hours / record.total_visits).toFixed(2) : 0;
    });

    let filtered = Object.values(reportMap);
    if (rangeSelectedBehaviour) filtered = filtered.filter(r => r.buying_behaviour === rangeSelectedBehaviour);
    if (rangeSelectedCustomerName) filtered = filtered.filter(r => r.customer_name.toLowerCase().includes(rangeSelectedCustomerName.toLowerCase()));

    return filtered.sort((a, b) => b.total_visits - a.total_visits);
  };

  const rangeReport = processRangeData();
  const rangeUniqueBehaviours = [...new Set(rangeData.map((c) => c.buying_behaviour).filter(Boolean))].sort();
  const rangeUniqueNames = [...new Set(rangeData.map((c) => c.member_name).filter(Boolean))].sort();

  const handleDownloadPDF = async () => {
    try {
      const payload = {
        date: selectedDate,
        member_name: selectedId || undefined,
        status: selectedStatus !== "all" ? selectedStatus : undefined,
        buying_behaviour: selectedBehaviour || undefined,
        pricing: selectedPricing || undefined
      };
      
      const response = await download_member_pdf(payload);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `customer_visits_${selectedDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF Download error:", error);
      alert("Failed to download PDF. No records matched your current filters.");
    }
  };

  const presentCount = customers.filter(c => c.status === "Present").length;
  const totalHours = filteredCustomers.reduce((sum, c) => sum + (c.total_visit_hours || 0), 0).toFixed(2);
  const totalVisitsInRange = rangeReport.reduce((sum, r) => sum + r.total_visits, 0);

  return (
    <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", padding: "24px", position: "relative" }}>
      
      {/* FULL SCREEN IMAGE MODAL */}
      {fullScreenImg && (
        <div
          onClick={() => setFullScreenImg(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out"
          }}
        >
          <img
            src={fullScreenImg}
            alt="Fullscreen View"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
            }}
          />
        </div>
      )}

      <div style={{ marginBottom: "24px" }}>
        
        <div style={{ display: "flex", gap: "8px", marginBottom: "24px", borderBottom: "2px solid #e5e7eb" }}>
          <button onClick={() => setActiveView("daily")} style={{ padding: "12px 24px", backgroundColor: "transparent", color: activeView === "daily" ? "#4f46e5" : "#6b7280", border: "none", borderBottom: activeView === "daily" ? "3px solid #4f46e5" : "none", cursor: "pointer", fontWeight: "600", fontSize: "15px" }}>📅 Daily Walk-ins</button>
          <button onClick={() => setActiveView("range")} style={{ padding: "12px 24px", backgroundColor: "transparent", color: activeView === "range" ? "#4f46e5" : "#6b7280", border: "none", borderBottom: activeView === "range" ? "3px solid #4f46e5" : "none", cursor: "pointer", fontWeight: "600", fontSize: "15px" }}>📊 Frequency Report</button>
        </div>

        {activeView === "daily" && (
          <>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", backgroundColor: "white" }} />
              <button onClick={fetchDataByDate} style={{ padding: "10px 20px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px" }}>🔍 Search</button>
            </div>

            {customers.length > 0 && (
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", minWidth: "150px" }}>
                  <option value="">All Members</option>
                  {uniqueCustomerIds.map((id) => (
                    <option key={id} value={id}>{customers.find(m => m.member_id === id)?.member_name} ({id})</option>
                  ))}
                </select>

                <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", minWidth: "140px" }}>
                  <option value="all">All Status</option>
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                </select>

                <select value={selectedBehaviour} onChange={(e) => setSelectedBehaviour(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", minWidth: "140px" }}>
                  <option value="">All Behaviours</option>
                  {uniqueBehaviours.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>

                <select value={selectedPricing} onChange={(e) => setSelectedPricing(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", minWidth: "140px" }}>
                  <option value="">All Pricing Tiers</option>
                  {uniquePricing.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>

                <button onClick={() => { setSelectedId(""); setSelectedStatus("all"); setSelectedBehaviour(""); setSelectedPricing(""); }} style={{ padding: "10px 16px", backgroundColor: "#6b7280", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" }}>↺ Reset</button>
              </div>
            )}
          </>
        )}

        {activeView === "range" && (
          <>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
              <input type="date" value={rangeStartDate} onChange={(e) => setRangeStartDate(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0" }} />
              <input type="date" value={rangeEndDate} onChange={(e) => setRangeEndDate(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0" }} />
              <button onClick={fetchDateRangeReport} style={{ padding: "10px 20px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" }}>📊 Generate Report</button>
            </div>

            {rangeData.length > 0 && (
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
                <select value={rangeSelectedBehaviour} onChange={(e) => setRangeSelectedBehaviour(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0" }}>
                  <option value="">Status</option>
                  {rangeUniqueBehaviours.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <select value={rangeSelectedCustomerName} onChange={(e) => setRangeSelectedCustomerName(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0" }}>
                  <option value="">All Members</option>
                  {rangeUniqueNames.map(n => <option key={n} value={n}>👤 {n}</option>)}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      {activeView === "daily" && (
        <>
          {!loading && filteredCustomers.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px", marginBottom: "24px" }}>
              <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>Total Walk-ins</div>
                <div style={{ fontSize: "28px", fontWeight: "600", color: "#10b981" }}>{presentCount}</div>
              </div>
              <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>Total Time (Hours)</div>
                <div style={{ fontSize: "28px", fontWeight: "600", color: "#f59e0b" }}>{totalHours}</div>
              </div>
            </div>
          )}

          {!loading && filteredCustomers.length > 0 && (
            <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: "20px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", color: "#374151" }}>ID</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", color: "#374151" }}>Name</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", color: "#374151" }}>Check-In</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", color: "#374151" }}>Check-Out</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Duration</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Status</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Behaviour</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Pricing</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((c, idx) => (
                    <React.Fragment key={c.member_id}>
                      <tr style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb", opacity: c.status === "Absent" ? 0.6 : 1 }}>
                        <td style={{ padding: "12px 16px", fontSize: "14px", fontWeight: "500" }}>{c.member_id}</td>
                        <td style={{ padding: "12px 16px", fontSize: "14px" }}>{c.member_name}</td>
                        <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666" }}>{c.check_in_time || "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666" }}>{c.check_out_time || "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666", textAlign: "center" }}>{c.total_visit_hours} hrs</td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "600", backgroundColor: c.status === "Present" ? "#d1fae5" : "#fee2e2", color: c.status === "Present" ? "#065f46" : "#991b1b" }}>
                            {c.status}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center", color: "#d97706", fontWeight: "600", fontSize: "12px" }}>{c.buying_behaviour || "—"}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center", color: "#059669", fontWeight: "600", fontSize: "12px" }}>{c.pricing || "—"}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <button onClick={() => setExpandedRow(expandedRow === c.member_id ? null : c.member_id)} style={{ padding: "4px 8px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>View</button>
                        </td>
                      </tr>
                      {expandedRow === c.member_id && (
                        <tr style={{ backgroundColor: "#f0f9ff", borderBottom: "1px solid #e5e7eb" }}>
                          <td colSpan="9" style={{ padding: "16px" }}>
                            <div style={{ display: "flex", gap: "20px", justifyContent: "center" }}>
                              {c.detected_face && (
                                <div>
                                  <p style={{ fontSize: "12px", fontWeight: "600" }}>Detected</p>
                                  <AsyncImage 
                                    src={`${REACT_APP_API_URL}${c.detected_face}`} 
                                    alt="Detected" 
                                    style={{ width: "100px", height: "100px", borderRadius: "6px", objectFit: "cover" }}
                                    onClick={() => setFullScreenImg(`${REACT_APP_API_URL}${c.detected_face}`)}
                                  />
                                </div>
                              )}
                              {c.original_face && (
                                <div>
                                  <p style={{ fontSize: "12px", fontWeight: "600" }}>Registered</p>
                                  <AsyncImage 
                                    src={`${REACT_APP_API_URL}${c.original_face}`} 
                                    alt="Registered" 
                                    style={{ width: "100px", height: "100px", borderRadius: "6px", objectFit: "cover" }}
                                    onClick={() => setFullScreenImg(`${REACT_APP_API_URL}${c.original_face}`)}
                                  />
                                </div>
                              )}
                              <div>
                                <p style={{ fontSize: "12px", fontWeight: "600" }}>Metrics</p>
                                <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                                  <div><strong>Gender:</strong> {c.gender || "—"}</div>
                                  <div><strong>Currency:</strong> {c.currency || "—"}</div>
                                  <div><strong>Store Branch:</strong> {c.branch_location || "—"}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Empty State Fallback */}
          {!loading && filteredCustomers.length === 0 && customers.length > 0 && (
            <div style={{ backgroundColor: "white", padding: "30px", textAlign: "center", borderRadius: "8px", color: "#6b7280", marginTop: "20px", border: "1px dashed #d1d5db" }}>
              No records match the selected filters.
            </div>
          )}

          {!loading && filteredCustomers.length > 0 && (
            <button onClick={handleDownloadPDF} style={{ padding: "10px 20px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px" }}>📄 Download PDF</button>
          )}
        </>
      )}

      {activeView === "range" && (
        <>
          {!rangeLoading && rangeReport.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px", marginBottom: "24px" }}>
              <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>Total Registered Customers Visited</div>
                <div style={{ fontSize: "28px", fontWeight: "600", color: "#1a1a1a" }}>{rangeReport.length}</div>
              </div>
              <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>Total Recorded Visits</div>
                <div style={{ fontSize: "28px", fontWeight: "600", color: "#4f46e5" }}>{totalVisitsInRange}</div>
              </div>
            </div>
          )}

          {!rangeLoading && rangeReport.length > 0 && (
            <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: "20px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", color: "#374151" }}>ID</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", color: "#374151" }}>Name</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Total Visits</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Avg Duration/Visit</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Behaviour</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>Pricing</th>
                  </tr>
                </thead>
                <tbody>
                  {rangeReport.map((r, idx) => (
                    <tr key={r.customer_id} style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
                      <td style={{ padding: "12px 16px", fontSize: "14px", fontWeight: "500" }}>{r.customer_id}</td>
                      <td style={{ padding: "12px 16px", fontSize: "14px" }}>{r.customer_name}</td>
                      <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666", textAlign: "center" }}><span style={{ padding: "4px 10px", borderRadius: "4px", backgroundColor: "#d1fae5", color: "#065f46", fontWeight: "600" }}>{r.total_visits}</span></td>
                      <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666", textAlign: "center" }}>{r.avg_duration} hrs</td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "#d97706", textAlign: "center", fontWeight: "600" }}>{r.buying_behaviour || "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "#059669", textAlign: "center", fontWeight: "600" }}>{r.pricing || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
